
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ListingDetail } from "../lib/jlApi";
import { readJSON, writeJSON } from "../lib/safeStorage";

const KEY = "db_favorites_v1";

export interface Favorite {
  listing: ListingDetail;
  savedAt: number; // epoch ms
}

type FavMap = Record<string, Favorite>; // keyed by MLS listingId

// Persisted favorites keyed by MLS number. Stores a full snapshot of the
// listing so a saved home survives even when it's off the current viewport.
export function useFavorites() {
  const [favorites, setFavorites] = useState<FavMap>(() =>
    readJSON<FavMap>(KEY, {})
  );

  useEffect(() => {
    writeJSON(KEY, favorites);
  }, [favorites]);

  const isFavorite = useCallback(
    (listingId: string) => Boolean(favorites[listingId]),
    [favorites]
  );

  const toggle = useCallback((listing: ListingDetail) => {
    const id = listing.listingId;
    if (!id) return;
    setFavorites((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = { listing, savedAt: Date.now() };
      return next;
    });
  }, []);

  const remove = useCallback((listingId: string) => {
    setFavorites((prev) => {
      if (!prev[listingId]) return prev;
      const next = { ...prev };
      delete next[listingId];
      return next;
    });
  }, []);

  const list = useMemo(
    () => Object.values(favorites).sort((a, b) => b.savedAt - a.savedAt),
    [favorites]
  );

  return { favorites, isFavorite, toggle, remove, list, count: list.length };
}
